import React, {useEffect} from 'react';
import moment from 'moment';
import {alpha, useTheme} from '@mui/material/styles';
import {FormattedMessage, useIntl} from 'react-intl';
import {Checkbox} from 'wobi-web-common/dist/v2/index.js';
import TravelerCard from '../../../components/TravelerCard';
import InsuredFooter from '../../../components/InsuredFooter';
import {fireGtmEvent} from '../../../utils/marketing.utils';
import {getOffersFields, getDetailForm} from '../../../utils/persistOfferDetails';
import {createTravelerValidationSchema} from './helpers/Tab2_helpers/validationSchema';
import {scrollToFirstError} from '../../../utils/scrollToFirstError';
import './Tab2.css';

import PropTypes from 'prop-types';

const Tab2 = React.memo(
  ({
    idForAccessibility,
    className,
    submitCallback,
    validationCallback,
    formikSetField,
    formikHandleChange,
    formikErrors,
    formikValues,
    formikSetTouched,
    formikValidateForm,
    actionButton,
    onButtonClickOTP,
  }) => {
    const intl = useIntl();
    const policyHolderMinDOB = moment().subtract(95, 'year');
    const policyHolderMaxDOB = moment().subtract(0, 'year');
    const theme = useTheme();
    const [showErrors, setShowErrors] = React.useState(false);

    // OTP detail recovery
    useEffect(() => {
      const otpToken = getOffersFields('otpToken');
      if (formikValues.phoneFromSf && otpToken) {
        formikSetField('phone1', formikValues.phoneFromSf);
        fireGtmEvent('Detailrecovery', {
          travelers: formikValues.travelers,
        });
      }
    }, [formikValues.phoneFromSf]);

    const travelerCount = Math.min(
      Number(formikValues.policyholdNum) || formikValues.travelers.length,
      formikValues.travelers.length,
    );
    const createValidationObject = () => createTravelerValidationSchema({
      travelerCount,
      policyHolderMinDOB,
      policyHolderMaxDOB,
      messages: id => intl.formatMessage({id}),
    });
    const validationObject = createValidationObject();

    // Restore travelers from persisted form
    useEffect(() => {
      setTimeout(() => {
        try {
          const offersFields = getDetailForm().values;
          const travelers = offersFields.travelers;
          if (!travelers || !travelers.length && !formikValues.travelers?.length) {
            const travelersArray = [];
            for (let i = 0; i < 10; i++) {
              if (offersFields[`ID${i + 1}`]) {
                travelersArray.push({
                  ID: offersFields[`ID${i + 1}`],
                  dob: offersFields[`dob${i + 1}`],
                  firstName: offersFields[`firstName${i + 1}`],
                  lastName: offersFields[`lastName${i + 1}`],
                  gender: offersFields[`gender${i + 1}`],
                });
              }
            }
            formikSetField('travelers', travelersArray);
          } else if (travelers && travelers.length && !formikValues.travelers?.length) {
            formikSetField('travelers', travelers);
          }
        } catch {
          formikSetField('travelers', [{ID: '', dob: '', firstName: '', lastName: '', gender: ''}]);
        }
      }, 100);
    }, []);

    useEffect(() => {
      validationCallback(createValidationObject());
    }, [travelerCount]);

    const validateOtpFirstPhoneFromSfIsSame = async () => {
      const detailsForm = sessionStorage.getItem('travel.details-form');
      const detailsFormParsed = JSON.parse(detailsForm);
      const phoneFromWeb = detailsFormParsed?.values?.phone1;
      const phoneFromSf = detailsFormParsed?.values?.phoneFromSf;
      if (phoneFromWeb !== phoneFromSf) {
        formikSetField('phone1', phoneFromSf);
        sessionStorage.removeItem('travel.details-form');
        sessionStorage.removeItem('otpFirst');
        window.location.reload();
      }
    };

    const handleSubmit = async () => {
      await validationCallback(createValidationObject());

      const errors = await formikValidateForm();
      if (Object.keys(errors).length === 0) {
        fireGtmEvent('confirmPolicyOwners', {
          insuredNumber: formikValues.phone1,
          birthDate: formikValues.dob1,
          gender: formikValues.gender1,
        });
        const {values} = JSON.parse(sessionStorage.getItem('travel.details-form')) || {};
        const {phone1} = values || {};

        if (phone1 && phone1.length < 10) {
          formikSetField('phone1', phone1);
          formikSetTouched('phone1', true);
          window.rollbar.error(JSON.stringify({
            browser: window.navigator.appCodeName,
            browserVersion: window.navigator.appVersion,
            field: 'phone1',
            fieldValue: formikValues.phone1,
          }));
        } else {
          if (sessionStorage.getItem('otpFirst')) {
            validateOtpFirstPhoneFromSfIsSame();
          }
          submitCallback(2);
        }
      } else {
        setShowErrors(true);
        Object.keys(validationObject).forEach((field) => formikSetTouched(field, true));
        scrollToFirstError(errors);
      }
    };

    const handleAddTraveler = () => {
      const MAX_TRAVELERS = 10;
      if (formikValues.travelers.length >= MAX_TRAVELERS) return;
      fireGtmEvent('TravelAddPassanger');
      const nextIndex = formikValues.travelers.length + 1;
      formikSetField('policyholdNum', nextIndex);
      formikSetField('travelers', [
        ...formikValues.travelers,
        {
          ID: formikValues['ID' + nextIndex] || '',
          firstName: formikValues['firstName' + nextIndex] || '',
          lastName: formikValues['lastName' + nextIndex] || '',
          dob: formikValues['dob' + nextIndex] || '',
          gender: formikValues['gender' + nextIndex] || '',
        },
      ]);
      formikSetField(`ID${nextIndex}`, '');
      formikSetField(`dob${nextIndex}`, '');
      formikSetField(`firstName${nextIndex}`, '');
      formikSetField(`lastName${nextIndex}`, '');
      formikSetField(`gender${nextIndex}`, '');
    };

    const handleBack = () => {
      window.location.hash = '#destination';
    };

    const hideClass = className === null ? undefined : 'tab2-hidden';

    if (className) {
      return <div className={className} id={idForAccessibility} role='tabpanel' style={{display: 'none'}} />;
    }

    return (
      <div id={idForAccessibility} role='tabpanel' className='tab2-main insured-main' style={{
        '--insured-surface': theme.palette.background.paper,
        '--insured-heading-color': theme.palette.secondary.main,
        '--insured-accent-color': theme.palette.primary.dark,
        '--insured-error-color': theme.palette.error.main,
        '--insured-copy-color': theme.palette.text.secondary,
        '--insured-border-color': theme.palette.divider,
        '--insured-selected-surface': theme.palette.action.selected,
        '--insured-heading-size': theme.typography.pxToRem(34),
        '--insured-heading-size-mobile': theme.typography.pxToRem(26),
        '--insured-copy-size': theme.typography.pxToRem(17),
        '--insured-copy-size-mobile': theme.typography.pxToRem(15),
        '--insured-action-size': theme.typography.pxToRem(17),
        '--insured-icon-size': theme.typography.pxToRem(24),
        '--insured-weight-black': theme.typography.fontWeightBlack,
        '--insured-weight-bold': theme.typography.fontWeightBold,
        '--insured-weight-light': theme.typography.fontWeightLight,
        '--insured-card-radius': `${theme.shape.borderRadius * 4}px`,
        '--insured-add-shadow': theme.shadows[2],
        '--travel-footer-background': theme.palette.background.paper,
        '--travel-footer-shadow': `0 -6px 20px ${alpha(theme.palette.secondary.dark, 0.1)}`,
      }}>
        <section className='insured-content'>
          <h1 className='insured-heading'>
            <FormattedMessage id='travelers.heading'/>
            <span className='insured-heading-accent' aria-hidden='true' />
          </h1>
          <p className='insured-subtitle'>
            <FormattedMessage id='travelers.subtitle'/>
          </p>

          <div className='insured-card-list'>
            {(formikValues.travelers || []).map((t, i) => (
              <TravelerCard
                key={i + (formikValues.travelers || []).length}
                index={i + 1}
                formikValues={formikValues}
                formikSetField={formikSetField}
                formikErrors={formikErrors}
                showErrors={showErrors}
                intl={intl}
                onIdentify={onButtonClickOTP}
              />
            ))}

            {/* Add traveler button */}
            {formikValues.travelers && formikValues.travelers.length < 10 && (
              <button
                type='button'
                onClick={handleAddTraveler}
                className='insured-add-button'
              >
                <span className='insured-add-icon' aria-hidden='true'>
                  +
                </span>
                <span className='insured-add-label'>
                  <FormattedMessage id='step1.tab2.addTravelerButton'/>
                </span>
              </button>
            )}
          </div>

          <div className='insured-confirmation'>
            <Checkbox
              checked={formikValues.isLeftIsrael}
              onChange={(e) => formikSetField('isLeftIsrael', e.target.checked)}
              label={intl.formatMessage({id: 'step1.tab2.isLeftIsrael'})}
              invalid={showErrors && !formikValues.isLeftIsrael}
              style={{color: theme.palette.text.primary}}
              data-testid='is-left-israel'
            />
            {showErrors && !formikValues.isLeftIsrael && (
              <div className='insured-confirmation-error' data-testid='is-left-israel-error'>
                {intl.formatMessage({id: 'step1.tab2.isLeftIsrael.error'})}
              </div>
            )}
          </div>

          {/* Footer */}
          <InsuredFooter
            onBack={handleBack}
            onNext={handleSubmit}
            nextLabel={intl.formatMessage({id: 'continue'})}
          />
        </section>
      </div>
    );
  },
);

Tab2.displayName = 'Tab2';

Tab2.propTypes = {
  idForAccessibility: PropTypes.string,
  className: PropTypes.string,
  submitCallback: PropTypes.func,
  validationCallback: PropTypes.func,
  formikSetField: PropTypes.func,
  formikHandleChange: PropTypes.func,
  formikErrors: PropTypes.object,
  formikValues: PropTypes.object,
  formikSetTouched: PropTypes.func,
  formikValidateForm: PropTypes.func,
  actionButton: PropTypes.func,
  onButtonClickOTP: PropTypes.func,
};

export default Tab2;
